import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AdminDashboardLayout from '../../components/layout/AdminDashboardLayout';
import DataTable from '../../components/common/DataTable';
import usePageTitle from '../../hooks/usePageTitle';
import useSmartRefresh from '../../hooks/useSmartRefresh';

/**
 * Support Page - Handle customer support tickets
 */
const SupportPage = () => {
  // Set page title
  usePageTitle('Support Center');

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');

  // Mock data fetching function
  const fetchSupportData = async () => {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    console.log('Support data refreshed');
  };

  const { isRefreshing, refreshData } = useSmartRefresh(
    fetchSupportData,
    'Support tickets refreshed successfully'
  );

  // Mock support tickets data
  const supportTickets = [
    {
      id: 'TKT-001',
      subject: 'IMEI Check Not Working',
      message: 'I tried to check my IMEI but the system shows an error. Can you please help me?',
      customerName: 'John Doe',
      customerEmail: 'john.doe@example.com',
      customerPhone: '+1 (555) 123-4567',
      priority: 'high',
      status: 'open',
      category: 'technical',
      createdAt: '2025-01-15 14:30',
      updatedAt: '2025-01-15 14:30',
      assignedTo: null,
      responses: []
    },
    {
      id: 'TKT-002',
      subject: 'Billing Question',
      message: 'I was charged twice for the same service. Please check my account and refund the duplicate charge.',
      customerName: 'Jane Smith',
      customerEmail: 'jane.smith@example.com',
      customerPhone: '+1 (555) 234-5678',
      priority: 'medium',
      status: 'in_progress',
      category: 'billing',
      createdAt: '2025-01-14 16:45',
      updatedAt: '2025-01-15 09:20',
      assignedTo: 'Support Agent 1',
      responses: [
        {
          id: 1,
          message: 'Thank you for contacting us. We are reviewing your account and will get back to you shortly.',
          author: 'Support Agent 1',
          timestamp: '2025-01-15 09:20',
          type: 'admin'
        }
      ]
    },
    {
      id: 'TKT-003',
      subject: 'Account Access Issue',
      message: 'I cannot log into my account. I tried resetting my password but did not receive the email.',
      customerName: 'Mike Johnson',
      customerEmail: 'mike.johnson@example.com',
      customerPhone: '+1 (555) 345-6789',
      priority: 'high',
      status: 'resolved',
      category: 'account',
      createdAt: '2025-01-13 11:15',
      updatedAt: '2025-01-14 10:30',
      assignedTo: 'Support Agent 2',
      responses: [
        {
          id: 1,
          message: 'We have reset your password and sent you a new temporary password via email. Please check your spam folder as well.',
          author: 'Support Agent 2',
          timestamp: '2025-01-14 10:30',
          type: 'admin'
        },
        {
          id: 2,
          message: 'Thank you! I received the email and was able to log in successfully.',
          author: 'Mike Johnson',
          timestamp: '2025-01-14 11:45',
          type: 'customer'
        }
      ]
    },
    {
      id: 'TKT-004',
      subject: 'Feature Request',
      message: 'It would be great if you could add support for checking multiple IMEIs at once. This would save a lot of time.',
      customerName: 'Sarah Wilson',
      customerEmail: 'sarah.wilson@example.com',
      customerPhone: '+1 (555) 456-7890',
      priority: 'low',
      status: 'open',
      category: 'feature_request',
      createdAt: '2025-01-12 09:30',
      updatedAt: '2025-01-12 09:30',
      assignedTo: null,
      responses: []
    },
    {
      id: 'TKT-005',
      subject: 'Service Unavailable',
      message: 'The unlock service for my Samsung device is showing as unavailable. When will it be back?',
      customerName: 'David Brown',
      customerEmail: 'david.brown@example.com',
      customerPhone: '+1 (555) 567-8901',
      priority: 'medium',
      status: 'closed',
      category: 'service',
      createdAt: '2025-01-10 15:20',
      updatedAt: '2025-01-11 14:15',
      assignedTo: 'Support Agent 1',
      responses: [
        {
          id: 1,
          message: 'The Samsung unlock service is temporarily unavailable due to maintenance. It should be back online within 24-48 hours.',
          author: 'Support Agent 1',
          timestamp: '2025-01-11 14:15',
          type: 'admin'
        }
      ]
    }
  ];

  // Calculate summary stats
  const totalTickets = supportTickets.length;
  const openTickets = supportTickets.filter(ticket => ticket.status === 'open').length;
  const inProgressTickets = supportTickets.filter(ticket => ticket.status === 'in_progress').length;
  const resolvedTickets = supportTickets.filter(ticket => ticket.status === 'resolved' || ticket.status === 'closed').length;

  // Define table columns
  const columns = [
    {
      id: 'id',
      label: 'Ticket ID',
      sortable: true,
      width: '12%',
      render: (value, item) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">{value}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{item.createdAt}</div>
        </div>
      )
    },
    {
      id: 'subject',
      label: 'Subject',
      sortable: true,
      width: '25%',
      render: (value, item) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{value}</div>
          <div className="text-sm text-gray-600 dark:text-gray-400 truncate">{item.customerName}</div>
        </div>
      )
    },
    {
      id: 'category',
      label: 'Category',
      sortable: true,
      width: '12%',
      render: (value) => {
        const categoryConfig = {
          technical: { 
            bg: 'bg-blue-100 dark:bg-blue-900/30', 
            text: 'text-blue-700 dark:text-blue-400',
            label: 'Technical'
          },
          billing: { 
            bg: 'bg-emerald-100 dark:bg-emerald-900/30', 
            text: 'text-emerald-700 dark:text-emerald-400',
            label: 'Billing'
          },
          account: { 
            bg: 'bg-purple-100 dark:bg-purple-900/30', 
            text: 'text-purple-700 dark:text-purple-400',
            label: 'Account'
          },
          service: { 
            bg: 'bg-orange-100 dark:bg-orange-900/30', 
            text: 'text-orange-700 dark:text-orange-400',
            label: 'Service'
          },
          feature_request: { 
            bg: 'bg-gray-100 dark:bg-gray-800', 
            text: 'text-gray-700 dark:text-gray-400',
            label: 'Feature'
          }
        };

        const config = categoryConfig[value] || categoryConfig.technical;

        return (
          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
            {config.label}
          </span>
        );
      }
    },
    {
      id: 'priority',
      label: 'Priority',
      sortable: true,
      align: 'center',
      width: '10%',
      render: (value) => {
        const priorityConfig = {
          high: { 
            bg: 'bg-error-100 dark:bg-error-900/30', 
            text: 'text-error-700 dark:text-error-400',
            icon: '🔴'
          },
          medium: { 
            bg: 'bg-warning-100 dark:bg-warning-900/30', 
            text: 'text-warning-700 dark:text-warning-400',
            icon: '🟡'
          },
          low: { 
            bg: 'bg-success-100 dark:bg-success-900/30', 
            text: 'text-success-700 dark:text-success-400',
            icon: '🟢'
          }
        };

        const config = priorityConfig[value] || priorityConfig.medium;

        return (
          <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
            <span>{config.icon}</span>
            <span className="capitalize">{value}</span>
          </span>
        );
      }
    },
    {
      id: 'status',
      label: 'Status',
      sortable: true,
      align: 'center',
      width: '12%',
      render: (value) => {
        const statusConfig = {
          open: { 
            bg: 'bg-error-100 dark:bg-error-900/30', 
            text: 'text-error-700 dark:text-error-400',
            icon: '●'
          },
          in_progress: { 
            bg: 'bg-warning-100 dark:bg-warning-900/30', 
            text: 'text-warning-700 dark:text-warning-400',
            icon: '●'
          },
          resolved: { 
            bg: 'bg-success-100 dark:bg-success-900/30', 
            text: 'text-success-700 dark:text-success-400',
            icon: '●'
          },
          closed: { 
            bg: 'bg-gray-100 dark:bg-gray-800', 
            text: 'text-gray-700 dark:text-gray-400',
            icon: '●'
          }
        };

        const config = statusConfig[value] || statusConfig.open;

        return (
          <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
            <span>{config.icon}</span>
            <span className="capitalize">{value.replace('_', ' ')}</span>
          </span>
        );
      }
    },
    {
      id: 'assignedTo',
      label: 'Assigned To',
      sortable: true,
      width: '15%',
      render: (value) => (
        <span className="text-gray-900 dark:text-gray-100">
          {value || 'Unassigned'}
        </span>
      )
    },
    {
      id: 'updatedAt',
      label: 'Last Updated',
      sortable: true,
      width: '14%',
      render: (value) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">{value}</div>
      )
    }
  ];

  // Define table actions
  const actions = [
    {
      type: 'view',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      tooltip: 'View Ticket',
      variant: 'primary'
    },
    {
      type: 'reply',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        </svg>
      ),
      tooltip: 'Reply to Ticket'
    }
  ];

  // Define filters
  const filters = [
    {
      key: 'status',
      label: 'Status',
      options: [
        { value: 'open', label: 'Open' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'resolved', label: 'Resolved' },
        { value: 'closed', label: 'Closed' }
      ]
    },
    {
      key: 'priority',
      label: 'Priority',
      options: [
        { value: 'high', label: 'High' },
        { value: 'medium', label: 'Medium' },
        { value: 'low', label: 'Low' }
      ]
    },
    {
      key: 'category',
      label: 'Category',
      options: [
        { value: 'technical', label: 'Technical' },
        { value: 'billing', label: 'Billing' },
        { value: 'account', label: 'Account' },
        { value: 'service', label: 'Service' },
        { value: 'feature_request', label: 'Feature Request' }
      ]
    }
  ];

  // Action handlers
  const handleView = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleReply = (ticket) => {
    setSelectedTicket(ticket);
    setIsReplyModalOpen(true);
  };

  const handleSendReply = () => {
    if (replyMessage.trim()) {
      console.log('Sending reply to ticket:', selectedTicket.id, replyMessage);
      // Add reply logic here
      setReplyMessage('');
      setIsReplyModalOpen(false);
      setSelectedTicket(null);
    }
  };

  const handleStatusChange = (ticketId, newStatus) => {
    console.log('Changing status for ticket:', ticketId, 'to:', newStatus);
    // Add status change logic here
  };

  return (
    <AdminDashboardLayout pageName="Support Center">
      <div className="space-y-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            Manage customer support tickets and provide assistance
          </p>
        </motion.div>

        {/* Summary Cards */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tickets</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{totalTickets}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-error-100 dark:bg-error-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-error-600 dark:text-error-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Open Tickets</p>
                <p className="text-2xl font-bold text-error-600 dark:text-error-400">{openTickets}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-warning-100 dark:bg-warning-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-warning-600 dark:text-warning-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">In Progress</p>
                <p className="text-2xl font-bold text-warning-600 dark:text-warning-400">{inProgressTickets}</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-success-100 dark:bg-success-900/30 rounded-xl flex items-center justify-center mr-4">
                <svg className="w-6 h-6 text-success-600 dark:text-success-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Resolved</p>
                <p className="text-2xl font-bold text-success-600 dark:text-success-400">{resolvedTickets}</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* DataTable Component */}
        <DataTable
          title="Support Tickets"
          columns={columns}
          data={supportTickets}
          actions={actions}
          onView={handleView}
          onReply={handleReply}
          onRefresh={refreshData}
          searchPlaceholder="Search tickets, customers, subjects..."
          filters={filters}
          rowsPerPageOptions={[5, 10, 20, 50]}
          defaultRowsPerPage={10}
          exportEnabled={true}
          refreshEnabled={true}
          isLoading={isRefreshing}
          className="mt-6"
        />
      </div>

      {/* Ticket View Modal */}
      <AnimatePresence>
        {selectedTicket && !isReplyModalOpen && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
              onClick={() => setSelectedTicket(null)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-4xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <h2 className="text-2xl font-semibold text-sky-600 dark:text-sky-400">Support Ticket</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{selectedTicket.id}</p>
                  </div>
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
                  {/* Ticket Details */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Ticket Information</h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Subject</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{selectedTicket.subject}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Category</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{selectedTicket.category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Priority</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100 capitalize">{selectedTicket.priority}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Status</p>
                          <select
                            value={selectedTicket.status}
                            onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                            className="mt-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors"
                          >
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Customer Information</h3>
                      <div className="space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Name</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{selectedTicket.customerName}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Email</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{selectedTicket.customerEmail}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{selectedTicket.customerPhone}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Assigned To</p>
                          <p className="text-sm text-gray-900 dark:text-gray-100">{selectedTicket.assignedTo || 'Unassigned'}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Original Message */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Original Message</h3>
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                      <p className="text-sm text-gray-700 dark:text-gray-300">{selectedTicket.message}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{selectedTicket.createdAt}</p>
                    </div>
                  </div>

                  {/* Conversation History */}
                  {selectedTicket.responses && selectedTicket.responses.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Conversation History</h3>
                      <div className="space-y-4">
                        {selectedTicket.responses.map((response) => (
                          <div
                            key={response.id}
                            className={`p-4 rounded-lg ${
                              response.type === 'admin'
                                ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500'
                                : 'bg-gray-50 dark:bg-gray-800 border-l-4 border-gray-300 dark:border-gray-600'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {response.author}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {response.timestamp}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{response.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
                  <button
                    onClick={() => handleReply(selectedTicket)}
                    className="px-4 py-2 bg-gradient-to-r from-sky-500 to-blue-600 text-white rounded-lg font-medium hover:from-sky-600 hover:to-blue-700 transition-all duration-300"
                  >
                    Reply to Customer
                  </button>
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reply Modal */}
      <AnimatePresence>
        {isReplyModalOpen && selectedTicket && (
          <motion.div
            className="fixed inset-0 z-50 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm"
              onClick={() => setIsReplyModalOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            <div className="flex items-center justify-center min-h-screen px-4 py-8">
              <motion.div
                className="relative w-full max-w-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl overflow-hidden transition-colors duration-300"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Reply to Ticket</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{selectedTicket.id} - {selectedTicket.subject}</p>
                  </div>
                  <button
                    onClick={() => setIsReplyModalOpen(false)}
                    className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Reply Message
                    </label>
                    <textarea
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors resize-none"
                      placeholder="Type your reply to the customer..."
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700 space-x-3">
                  <button
                    onClick={handleSendReply}
                    disabled={!replyMessage.trim()}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                      replyMessage.trim()
                        ? 'bg-gradient-to-r from-sky-500 to-blue-600 text-white hover:from-sky-600 hover:to-blue-700'
                        : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Send Reply
                  </button>
                  <button
                    onClick={() => setIsReplyModalOpen(false)}
                    className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </AdminDashboardLayout>
  );
};

export default SupportPage;